import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthProvider'
import { useMobilePolicies } from '@/hooks/useMobilePolicies'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Loader2, ArrowLeft, ShieldCheck, FileText, Download, CheckCircle2,
  AlertTriangle, Clock, ChevronDown, ChevronUp, X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'

const categoryConfig = {
  health_safety: { label: 'Health & Safety', color: 'bg-red-50 text-red-700 border-red-200' },
  hr: { label: 'HR', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  operations: { label: 'Operations', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  compliance: { label: 'Compliance', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  legal: { label: 'Legal', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  other: { label: 'Other', color: 'bg-gray-50 text-gray-600 border-gray-200' },
}

const statusConfig = {
  overdue: { label: 'Overdue', icon: AlertTriangle, color: 'bg-red-50 text-red-700 border-red-200', cardBorder: 'border-red-300' },
  pending: { label: 'Pending', icon: Clock, color: 'bg-amber-50 text-amber-700 border-amber-200', cardBorder: 'border-amber-300' },
  acknowledged: { label: 'Acknowledged', icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-700 border-emerald-200', cardBorder: 'border-gray-200' },
}

export default function PoliciesPage() {
  const navigate = useNavigate()
  const { userProfile } = useAuth()
  const { policies, pendingCount, overdueCount, loading, acknowledgePolicy } = useMobilePolicies(userProfile?.id)
  const [filter, setFilter] = useState('all')
  const [expandedId, setExpandedId] = useState(null)
  const [acknowledging, setAcknowledging] = useState(null)
  const [confirmId, setConfirmId] = useState(null)

  const filteredPolicies = filter === 'all'
    ? policies
    : policies.filter(p => p.status === filter)

  const handleAcknowledge = async (policy) => {
    setAcknowledging(policy.id)
    try {
      const result = await acknowledgePolicy(policy.id)
      if (result) {
        toast.success('Policy acknowledged')
        setConfirmId(null)
      }
    } catch {
      toast.error('Failed to acknowledge policy')
    } finally {
      setAcknowledging(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/more')} className="p-1 -ml-1 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <h1 className="text-xl font-bold text-gray-900">Policies</h1>
      </div>

      {/* Summary bar */}
      {(pendingCount > 0 || overdueCount > 0) && (
        <div className={cn(
          'rounded-xl border px-4 py-2.5 flex items-center gap-3',
          overdueCount > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
        )}>
          {overdueCount > 0 ? (
            <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
          ) : (
            <Clock className="h-4 w-4 text-amber-500 shrink-0" />
          )}
          <p className="text-xs text-gray-700">
            {overdueCount > 0 && (
              <span className="font-medium text-red-700">{overdueCount} overdue</span>
            )}
            {overdueCount > 0 && pendingCount > 0 && ' and '}
            {pendingCount > 0 && (
              <span className="font-medium text-amber-700">{pendingCount} pending</span>
            )}
            {' '}
            {(overdueCount + pendingCount) === 1 ? 'policy requires' : 'policies require'} your acknowledgment.
          </p>
        </div>
      )}

      {policies.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <ShieldCheck className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No policies assigned.</p>
          <p className="text-xs text-gray-400 mt-1">Policies assigned to you will appear here.</p>
        </div>
      ) : (
        <>
          {/* Filter chips */}
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-4 px-4 no-scrollbar">
            <button
              onClick={() => setFilter('all')}
              className={cn(
                'shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                filter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'
              )}
            >
              All ({policies.length})
            </button>
            {['pending', 'overdue', 'acknowledged'].map(status => {
              const count = policies.filter(p => p.status === status).length
              if (!count) return null
              return (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={cn(
                    'shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                    filter === status ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'
                  )}
                >
                  {statusConfig[status].label} ({count})
                </button>
              )
            })}
          </div>

          {/* Policy list */}
          <div className="space-y-2">
            {filteredPolicies.map(policy => {
              const sCfg = statusConfig[policy.status] || statusConfig.pending
              const cCfg = categoryConfig[policy.category] || categoryConfig.other
              const StatusIcon = sCfg.icon
              const isExpanded = expandedId === policy.id

              return (
                <div key={policy.id} className={cn('bg-white rounded-xl border overflow-hidden', sCfg.cardBorder)}>
                  {/* Main row */}
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : policy.id)}
                    className="w-full text-left px-4 py-3.5 flex gap-3"
                  >
                    <div className={cn('shrink-0 w-9 h-9 rounded-lg flex items-center justify-center mt-0.5',
                      policy.status === 'acknowledged' ? 'bg-emerald-50' : policy.status === 'overdue' ? 'bg-red-50' : 'bg-amber-50'
                    )}>
                      <StatusIcon className={cn('h-4 w-4',
                        policy.status === 'acknowledged' ? 'text-emerald-500' : policy.status === 'overdue' ? 'text-red-500' : 'text-amber-500'
                      )} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-gray-900 leading-snug">{policy.title}</p>
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className={cn('text-[10px]', cCfg.color)}>
                          {cCfg.label}
                        </Badge>
                        <span className="text-[10px] text-gray-400">{policy.version}</span>
                        {policy.status !== 'acknowledged' && policy.due_date && (
                          <span className={cn('text-[10px]',
                            policy.status === 'overdue' ? 'text-red-500 font-medium' : 'text-gray-400'
                          )}>
                            Due {new Date(policy.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </span>
                        )}
                        {policy.status === 'acknowledged' && policy.acknowledged_date && (
                          <span className="text-[10px] text-emerald-600">
                            Signed {new Date(policy.acknowledged_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
                      {policy.description && (
                        <p className="text-xs text-gray-600 leading-relaxed">{policy.description}</p>
                      )}

                      {policy.effective_date && (
                        <p className="text-[11px] text-gray-400">
                          Effective from {new Date(policy.effective_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      )}

                      <div className="flex gap-2">
                        {policy.document_url && (
                          <a
                            href={policy.document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs text-emerald-600 font-medium hover:text-emerald-700 transition-colors bg-emerald-50 px-3 py-1.5 rounded-lg"
                          >
                            <Download className="h-3 w-3" />
                            View Document
                          </a>
                        )}
                      </div>

                      {policy.requires_acknowledgment && policy.status !== 'acknowledged' && (
                        <div className="pt-1">
                          {policy.acknowledgment_statement && (
                            <div className="bg-gray-50 rounded-lg p-3 mb-3 border border-gray-100">
                              <p className="text-[11px] text-gray-600 leading-relaxed">{policy.acknowledgment_statement}</p>
                            </div>
                          )}
                          <Button
                            size="sm"
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-xs h-9"
                            onClick={() => setConfirmId(policy.id)}
                            disabled={acknowledging === policy.id}
                          >
                            {acknowledging === policy.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                            ) : (
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                            )}
                            Acknowledge Policy
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* Confirmation overlay */}
      {confirmId && (() => {
        const policy = policies.find(p => p.id === confirmId)
        if (!policy) return null
        return (
          <>
            <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setConfirmId(null)} />
            <div className="fixed inset-x-4 bottom-0 z-50 pb-safe mb-24 bg-white rounded-2xl shadow-xl border border-gray-200 p-5 space-y-4 animate-in slide-in-from-bottom duration-200">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Confirm Acknowledgment</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{policy.title}</p>
                </div>
                <button onClick={() => setConfirmId(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              </div>

              {policy.acknowledgment_statement && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-800 leading-relaxed">{policy.acknowledgment_statement}</p>
                </div>
              )}

              <p className="text-xs text-gray-500">
                By acknowledging, you confirm you have read and understood this policy.
              </p>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs h-9"
                  onClick={() => setConfirmId(null)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-xs h-9"
                  onClick={() => handleAcknowledge(policy)}
                  disabled={acknowledging === policy.id}
                >
                  {acknowledging === policy.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  ) : (
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  I Acknowledge
                </Button>
              </div>
            </div>
          </>
        )
      })()}
    </div>
  )
}
